import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({
  user: null,
  userProfile: null,
  loading: true,
  profileLoading: false,
  signIn: async () => ({ data: null, error: { message: 'AuthProvider not mounted' } }),
  signOut: async () => ({ error: null }),
  updateProfile: async () => ({ data: null, error: { message: 'AuthProvider not mounted' } }),
  isAuthenticated: false,
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const mountedRef = useRef(true);
  const initDoneRef = useRef(false);

  const loadProfile = useCallback(async (userId) => {
    if (!userId || !mountedRef?.current) return;
    setProfileLoading(true);
    try {
      // Query user_profiles by primary key (id = auth.users.id)
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();

      if (!mountedRef?.current) return;

      if (error && error?.code === 'PGRST116') {
        // Profile missing — try to initialize via RPC
        try {
          const { error: rpcError } = await supabase?.rpc('initialize_player_stats');
          if (!rpcError) {
            const { data: retryData, error: retryError } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();
            if (mountedRef?.current && retryData && !retryError) {
              setUserProfile(retryData);
            }
          } else {
            console.error('[Auth] initialize_player_stats RPC error:', rpcError?.message);
          }
        } catch (initErr) {
          console.error('[Auth] Profile init error:', initErr);
        }
      } else if (!error && data) {
        setUserProfile(data);
      } else if (error) {
        console.error('[Auth] Profile load error:', error?.message, '| code:', error?.code);
      }
    } catch (err) {
      console.error('[Auth] Profile load exception:', err);
    } finally {
      if (mountedRef?.current) setProfileLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      async (event, session) => {
        if (!mountedRef?.current) return;

        console.log('[Auth] Event:', event, '| User:', session?.user?.email ?? 'none');

        const currentUser = session ? session?.user : null;
        setUser(currentUser);

        if (!initDoneRef?.current) {
          initDoneRef.current = true;
          setLoading(false);
        }

        if (currentUser) {
          loadProfile(currentUser?.id);
        } else {
          setUserProfile(null);
          setProfileLoading(false);
        }
      }
    );

    // Safety timeout: unblock UI if onAuthStateChange never fires
    const safetyTimer = setTimeout(() => {
      if (!initDoneRef?.current && mountedRef?.current) {
        console.warn('[Auth] Safety timeout — forcing loading=false');
        initDoneRef.current = true;
        setLoading(false);
      }
    }, 5000);

    return () => {
      mountedRef.current = false;
      subscription?.unsubscribe();
      clearTimeout(safetyTimer);
    };
  }, [loadProfile]);

  const signIn = async (email, password) => {
    try {
      if (!supabase || !supabase?.auth) {
        console.error('[SignIn] Supabase client not initialized');
        return { data: null, error: { message: 'Authentication service unavailable. Please refresh the page.' } };
      }
      // Direct call — no optional chaining on the method itself
      const result = await supabase?.auth?.signInWithPassword({
        email: (email || '')?.trim()?.toLowerCase(),
        password: password || '',
      });
      const data = result ? result?.data : null;
      const error = result ? result?.error : null;
      if (error) {
        console.error('[SignIn] Supabase error:', error?.message, '| Status:', error?.status);
      }
      return { data: data || null, error: error || null };
    } catch (err) {
      console.error('[SignIn] Caught exception:', err);
      return { data: null, error: { message: err?.message || 'Network error. Please try again.' } };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut();
      if (!error) {
        setUser(null);
        setUserProfile(null);
        setProfileLoading(false);
      }
      return { error };
    } catch (err) {
      return { error: { message: 'Network error. Please try again.' } };
    }
  };

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } };
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single();
      if (!error && data) setUserProfile(data);
      return { data, error };
    } catch (err) {
      return { error: { message: 'Network error. Please try again.' } };
    }
  };

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    signIn,
    signOut,
    updateProfile,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
